import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import MerchantSearchSelect from '../ui/merchant-search-select';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  Users, Repeat, TrendingUp, ShieldCheck, ArrowUpRight, RefreshCw, X, Loader2,
} from 'lucide-react';
import { getCountryName } from '../../utils/countries';
import { formatCurrencySelector } from '../../utils/currencyFormatter';

const IDENTITY_OPTIONS = [
  { value: 'email_card', label: 'Email + Card (strict)' },
  { value: 'email', label: 'Email only' },
  { value: 'card', label: 'Card only' },
];

const formatUSD = (n) => {
  if (n == null || isNaN(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Number(n).toLocaleString()}`;
};
const formatLocal = (n, cur) => {
  if (n == null || isNaN(n)) return '—';
  const v = Math.abs(n) >= 1_000 ? `${(n / 1_000).toFixed(1)}k` : Number(n).toLocaleString();
  return `${v} ${cur || ''}`.trim();
};
const formatPct = (n) => (n == null || isNaN(n) ? '—' : `${Number(n).toFixed(1)}%`);

const cohortColor = (v) => {
  if (v === null || v === undefined) return { bg: '#f8fafc', fg: '#cbd5e1' };
  if (v >= 80) return { bg: '#1e3a8a', fg: '#ffffff' };
  if (v >= 50) return { bg: '#1d4ed8', fg: '#ffffff' };
  if (v >= 30) return { bg: '#3b82f6', fg: '#ffffff' };
  if (v >= 15) return { bg: '#93c5fd', fg: '#1e3a8a' };
  if (v > 0) return { bg: '#dbeafe', fg: '#1e3a8a' };
  return { bg: '#f1f5f9', fg: '#94a3b8' };
};

const KpiCard = ({ label, value, sub, accent = 'default', testId }) => {
  const accents = {
    default: 'text-slate-900',
    good: 'text-emerald-600',
    warn: 'text-orange-600',
  };
  return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1">{label}</div>
        <div className={`text-2xl font-bold leading-tight ${accents[accent]}`}>{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const AnalyticsCustomers = ({
  // Shared filters from parent (Analytics.jsx)
  filters,
  handleFilterChange,
  clearFilters,
  hasActiveFilters,
  countries = [],
  currencies = [],
  processors = [],
  merchants = [],
  periodOptions = [],
  monthOptions = [],
  yearOptions = [],
  isExporting = false,
}) => {
  const { token, selectedPlatform, backendUrl } = useAuth();
  const navigate = useNavigate();
  const [identityMode, setIdentityMode] = useState('email_card');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Period is FORCED to "This Year" on this tab — full year of data is needed
  // for cohort retention to be meaningful, and the parent's shared period
  // filter doesn't apply here. We still read the other shared filters.
  const FORCED_PERIOD = 'thisyear';
  const country = filters?.country;
  const currency = filters?.currency;
  const processor = filters?.processor;
  const merchantFilter = filters?.merchant_id;

  const fetchData = useCallback(async () => {
    if (!token || !selectedPlatform) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        platform_id: selectedPlatform,
        period: FORCED_PERIOD,
        identity_mode: identityMode,
      });
      if (merchantFilter && merchantFilter !== 'all') params.set('merchant_id', merchantFilter);
      if (currency && currency !== 'all') params.set('currency', currency);
      if (country && country !== 'all') params.set('country', country);
      if (processor && processor !== 'all') params.set('processor', processor);
      const res = await axios.get(
        `${backendUrl}/api/analytics/recurring-customers?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (err) {
      console.error('[AnalyticsCustomers] fetch failed:', err);
      setError('Could not load recurring customer analytics.');
    } finally {
      setLoading(false);
    }
  }, [
    token, selectedPlatform, backendUrl, identityMode,
    country, currency, processor, merchantFilter,
  ]);

  // Keep a ref to the latest fetchData. Then drive the effect off the
  // primitive filter values only — this guarantees the effect never re-fires
  // due to upstream context re-renders that recreate object references.
  const fetchDataRef = useRef(fetchData);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => {
    fetchDataRef.current();
  }, [
    token, selectedPlatform, identityMode,
    country, currency, processor, merchantFilter,
  ]);

  const openCustomerInHistory = (row) => {
    const sp = new URLSearchParams();
    if (row.email) sp.set('customer_email', row.email);
    if (row.card_masked) sp.set('card_masked', row.card_masked);
    navigate(`/dashboard/transactions?${sp.toString()}`);
  };

  const k = data?.kpis;
  const monthly = data?.monthly_series || [];
  const cohort = data?.cohort_table || [];
  const freq = data?.frequency_distribution || [];
  const top = data?.top_recurring || [];

  // Defensive: if parent didn't supply filters, render an inline period+identity-only
  // header so the component never crashes when used standalone.
  const filtersReady = filters && handleFilterChange;

  return (
    <div className="space-y-6" data-testid="analytics-customers-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            Recurring Customers
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            New vs returning behaviour, retention cohorts, and your highest-value repeat customers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={identityMode} onValueChange={setIdentityMode}>
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="identity-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IDENTITY_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Shared filter bar — matches Approval Rates / Declined Reasons / Retry Waterfall */}
      {filtersReady && (
        <Card className="border-0 shadow-md bg-white" data-testid="customers-filters-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Period — locked to "This Year" on this tab */}
              <div className="flex items-center gap-2" data-testid="customers-period-locked">
                <span className="text-sm font-medium text-gray-600">Period:</span>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700 select-none">
                  <span>This Year</span>
                  <span
                    className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-600"
                    title="Recurring-customer analytics require a full-year window for cohort retention to be meaningful."
                  >
                    fixed
                  </span>
                </div>
              </div>

              {/* Country */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Country:</span>
                <Select value={filters.country} onValueChange={(v) => handleFilterChange('country', v)}>
                  <SelectTrigger className="w-40" data-testid="customers-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countries.filter(c => c && c.code).map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className={`fi fi-${c.code.toLowerCase()} mr-2`}></span>
                        {c.name || getCountryName(c.code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Currency */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Currency:</span>
                <Select value={filters.currency} onValueChange={(v) => handleFilterChange('currency', v)}>
                  <SelectTrigger className="w-32" data-testid="customers-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.from(new Set([
                      ...currencies.filter(c => c && typeof c === 'string'),
                      ...((filters.currency && filters.currency !== 'all') ? [filters.currency] : []),
                    ])).map(c => (
                      <SelectItem key={c} value={c}>{formatCurrencySelector(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Processor */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Processor:</span>
                <Select value={filters.processor} onValueChange={(v) => handleFilterChange('processor', v)}>
                  <SelectTrigger className="w-36" data-testid="customers-processor-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {processors.filter(p => p && typeof p === 'string').map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Merchant */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Merchant:</span>
                <MerchantSearchSelect
                  merchants={merchants}
                  value={filters.merchant_id}
                  onChange={(v) => handleFilterChange('merchant_id', v)}
                  placeholder="All Merchants"
                  className="w-48"
                />
              </div>

              {hasActiveFilters && clearFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                disabled={loading}
                className="flex items-center gap-2 ml-auto"
                data-testid="customers-refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-700" data-testid="rc-error">{error}</CardContent>
        </Card>
      )}

      {/* Content (with loading overlay) */}
      <div className="relative">
        {loading && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-lg"
            data-testid="rc-loading-overlay"
          >
            <div className="bg-white shadow-lg border border-slate-200 rounded-xl px-6 py-4 flex items-center gap-3">
              <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
              <div>
                <div className="text-sm font-semibold text-slate-800">Applying filters…</div>
                <div className="text-xs text-slate-500">Recomputing recurring-customer analytics</div>
              </div>
            </div>
          </div>
        )}

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="rc-kpis">
        <KpiCard
          label="Recurring rate"
          value={formatPct(k?.recurring_rate)}
          sub={`${(k?.recurring_customers ?? 0).toLocaleString()} of ${(k?.total_customers ?? 0).toLocaleString()}`}
          accent={(k?.recurring_rate || 0) >= 30 ? 'good' : 'default'}
          testId="kpi-recurring-rate"
        />
        <KpiCard
          label="Recurring revenue share"
          value={formatPct(k?.recurring_revenue_share)}
          sub={`${formatUSD(k?.revenue_recurring_usd)} of ${formatUSD(k?.revenue_total_usd)}`}
          accent={(k?.recurring_revenue_share || 0) >= 50 ? 'good' : 'default'}
          testId="kpi-recurring-revenue-share"
        />
        <KpiCard
          label="Avg revenue / recurring"
          value={formatUSD(k?.avg_revenue_per_recurring_usd)}
          sub={`${(k?.avg_txns_per_recurring ?? 0).toFixed(1)} avg transactions`}
          testId="kpi-avg-revenue-recurring"
        />
        <KpiCard
          label="One-time customers"
          value={(k?.one_time_customers ?? 0).toLocaleString()}
          sub={`${formatUSD(k?.revenue_one_time_usd)} revenue`}
          testId="kpi-one-time"
        />
        <KpiCard
          label="Identity mode"
          value={data?.identity_mode === 'email_card' ? 'Email+Card' : data?.identity_mode === 'card' ? 'Card' : 'Email'}
          sub="strictness of definition"
          testId="kpi-identity-mode"
        />
      </div>

      {/* Row 1: New vs Returning chart + Frequency distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm lg:col-span-2" data-testid="rc-newret-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowUpRight className="h-4 w-4 text-blue-600" />
              New vs Returning customers
            </CardTitle>
            <p className="text-xs text-slate-500">Monthly transaction counts split by acquisition state.</p>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#475569' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#475569' }} />
                  <Tooltip
                    formatter={(v) => Number(v).toLocaleString()}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="new_count" name="New" stackId="x" fill="#cbd5e1" isAnimationActive={!isExporting} />
                  <Bar dataKey="returning_count" name="Returning" stackId="x" fill="#2563eb" isAnimationActive={!isExporting} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="rc-freq-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Repeat className="h-4 w-4 text-emerald-600" />
              Frequency distribution
            </CardTitle>
            <p className="text-xs text-slate-500">Customer count by # of approved transactions.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {freq.map((b) => (
                <div key={b.bucket} className="flex items-center gap-3" data-testid={`freq-row-${b.bucket}`}>
                  <div className="w-12 text-xs font-medium text-slate-600">{b.bucket}</div>
                  <div className="flex-1 relative h-6 bg-slate-100 rounded overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded"
                      style={{ width: `${Math.min(100, b.customers_pct)}%` }}
                    />
                    <div className="absolute inset-0 flex items-center px-2 text-[11px] font-medium text-slate-700">
                      {b.customers.toLocaleString()} ({b.customers_pct.toFixed(1)}%)
                    </div>
                  </div>
                  <div className="w-20 text-right text-xs text-slate-600">{formatUSD(b.revenue_usd)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cohort heatmap */}
      <Card className="border-0 shadow-sm" data-testid="rc-cohort-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-indigo-600" />
            Cohort retention (last 12 months)
          </CardTitle>
          <p className="text-xs text-slate-500">
            % of each acquisition month's customers who returned in subsequent months. Darker = better retention.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cohort-table">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left font-medium p-2">Cohort</th>
                  <th className="text-right font-medium p-2">Size</th>
                  {['M+0', 'M+1', 'M+2', 'M+3', 'M+4', 'M+5'].map(h => (
                    <th key={h} className="text-center font-medium p-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohort.map(c => (
                  <tr key={c.cohort} className="border-t border-slate-100">
                    <td className="p-2 font-medium text-slate-700">{c.cohort}</td>
                    <td className="p-2 text-right text-slate-500">{c.size.toLocaleString()}</td>
                    {(c.retention_pct || []).map((v, idx) => {
                      const { bg, fg } = cohortColor(v);
                      return (
                        <td key={idx} className="p-0">
                          <div
                            className="mx-1 my-0.5 py-2 rounded text-center font-semibold"
                            style={{ background: bg, color: fg }}
                          >
                            {v === null || v === undefined ? '—' : `${v.toFixed(0)}%`}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top recurring customers */}
      <Card className="border-0 shadow-sm" data-testid="rc-top-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-amber-600" />
            Top recurring customers
          </CardTitle>
          <p className="text-xs text-slate-500">Ranked by approved revenue (USD). Click any row to open in Transaction History.</p>
        </CardHeader>
        <CardContent className="p-0">
          {top.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="rc-top-empty">
              No recurring customers in this window with the selected identity mode.
              {data?.identity_mode === 'email_card' && (
                <div className="text-xs mt-1">Try switching to <strong>Email only</strong> if the data uses different cards per visit.</div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="top-recurring-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Email / Card</th>
                    <th className="text-right p-3">Txns</th>
                    <th className="text-right p-3">Revenue (USD)</th>
                    <th className="text-right p-3">Local</th>
                    <th className="text-right p-3">Merchants</th>
                    <th className="text-right p-3">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((row, idx) => (
                    <tr
                      key={`${row.email}-${row.card_masked}-${idx}`}
                      onClick={() => openCustomerInHistory(row)}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      data-testid={`top-recurring-row-${idx}`}
                    >
                      <td className="p-3 font-medium text-slate-800">
                        {row.name || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-3 text-slate-600">
                        {row.email && <div className="break-all">{row.email}</div>}
                        {row.card_masked && (
                          <div className="text-xs text-slate-500">{row.card_masked}</div>
                        )}
                      </td>
                      <td className="p-3 text-right font-semibold text-slate-800">{row.txns}</td>
                      <td className="p-3 text-right font-semibold text-slate-800">{formatUSD(row.revenue_usd)}</td>
                      <td className="p-3 text-right text-slate-600">{formatLocal(row.revenue_local, row.currency)}</td>
                      <td className="p-3 text-right">
                        <Badge variant="secondary" className="text-xs">{row.merchants_count}</Badge>
                      </td>
                      <td className="p-3 text-right text-xs text-slate-500">
                        {row.last_seen ? new Date(row.last_seen).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      </div>
    </div>
  );
};

export default AnalyticsCustomers;
